import { WEEK_STARTS_ON_SUNDAY_COUNTRY_CODES } from '@calendly/booking-kit/constants'
import { isEqual, isNil, omitBy } from 'lodash-es'
import moment from 'moment-timezone'
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect'

import { getUser } from 'common'
import { determineMembers, membersToServerData } from 'components/event_type_edit'
import {
  getAvailableSpots,
  calculateDurationInMinutes,
  calculateEventsOverlap,
  getSelectedMemberEvents,
  timeRange,
  unavailabilityForDateRange,
} from 'features/one_off_modal'
import {
  countryCodeSelector,
  idSelector,
  timeNotationSelector,
} from 'selectors/current_user'
import { camelize, snakeize } from 'services/string_utils'

const currentTimeSelector = ({ bookModal }) => bookModal.currentTime
export const modalVisibleSelector = ({ bookModal }) => bookModal.modalVisible
export const stepSelector = ({ bookModal }) => bookModal.step
export const loadingEventTypeSelector = ({ bookModal }) => bookModal.loadingEventType
export const loadingEventTypeMembersSelector = ({ bookModal }) => bookModal.loadingEventTypeMembers
export const eventTypeIdSelector = ({ bookModal }) => bookModal.eventTypeId
export const eventTypeUuidSelector = ({ bookModal }) => bookModal.eventTypeUuid
export const eventTypeTypeSelector = ({ bookModal }) => bookModal.eventTypeType
export const eventTypeOwnerTypeSelector = ({ bookModal }) => bookModal.eventTypeOwnerType
export const eventTypeOwnerIdSelector = ({ bookModal }) => bookModal.eventTypeOwnerId
export const inviteesSelector = ({ bookModal }) => bookModal.invitees
export const guestsSelector = ({ bookModal }) => bookModal.guests
export const calendarStepSelector = ({ bookModal }) => bookModal.calendarStep
const calendarListSelector = ({ bookModal }) => bookModal.calendarList
export const inviteeSelectedTrackedSelector = ({ bookModal }) => bookModal.inviteeSelectedTracked
export const pendingTrackInviteeSelectedSelector = ({ bookModal }) => bookModal.pendingTrackInviteeSelected
export const bookErrorMsgSelector = ({ bookModal }) => bookModal.bookErrorMsg
export const stytchBlockContextSelector = ({ bookModal }) => bookModal.stytchBlockContext
export const isOneOffBookingSelector = ({ bookModal }) => bookModal.bookingType === 'one_off'
export const loadingScheduledEventSelector = ({ bookModal }) => bookModal.loadingScheduledEvent
export const sourceSelector = ({ bookModal }) => bookModal.source
export const contactsBookingFlowSelector = ({ bookModal }) => bookModal.contactsBookingFlow
export const showRecaptchaChallengeSelector = createSelector(
  ({ bookModal }) => bookModal.recaptcha,
  (recaptcha) => recaptcha?.showChallenge ?? false
)
export const recaptchaV2TokenSelector = createSelector(
  ({ bookModal }) => bookModal.recaptcha,
  (recaptcha) => recaptcha?.v2Token
)
const searchContactsSelector = ({ bookModal }) => bookModal.searchContacts
const hostsFieldSelector = createSelector(
  calendarStepSelector,
  ({ hostsField }) => hostsField
)

export const stytchTelemetryIdSelector = ({ bookModal }) => bookModal.stytchTelemetryId
export const stytchTelemetryTimestampSelector = ({ bookModal }) => bookModal.stytchTelemetryTimestamp

export const selectedMembersSelector = createSelector(
  hostsFieldSelector,
  ({ selectedMembers }) => selectedMembers
)
export const calendarViewSelector = createSelector(
  calendarStepSelector,
  isOneOffBookingSelector,
  ({ view }, isOneOff) => (isOneOff ? 'full_calendar' : view)
)
export const loadingMemberSuggestionsSelector = createSelector(
  hostsFieldSelector,
  ({ isLoading }) => isLoading
)
export const filteredMemberSuggestionsSelector = createSelector(
  hostsFieldSelector,
  ({ suggestions, selectedMembers }) => (
    suggestions.filter(({ email }) => !selectedMembers.some(({ email: selectedEmail }) => email === selectedEmail))
  )
)
export const loadingSearchContactsSelector = createSelector(
  searchContactsSelector,
  ({ isLoading }) => isLoading
)
export const searchContactsResultSelector = createSelector(
  searchContactsSelector,
  ({ contacts }) => contacts
)
export const datesRangeSelector = createSelector(
  calendarStepSelector,
  ({ rangeStart, rangeEnd }) => ({ rangeStart, rangeEnd })
)
export const selectedSpotSelector = createSelector(
  calendarStepSelector,
  ({ selectedSpot }) => selectedSpot
)
const fetchedEventsDaysRangeSelector = createSelector(
  calendarStepSelector,
  ({ fetchedEventsDaysRange }) => fetchedEventsDaysRange
)
export const loadingCalendarDataSelector = createSelector(
  calendarStepSelector,
  ({ loadingCalendarData }) => loadingCalendarData
)
export const durationFieldSelector = createSelector(
  calendarStepSelector,
  ({ durationField }) => durationField
)
export const eventTypeDurationSelector = createSelector(
  durationFieldSelector,
  calculateDurationInMinutes
)
const selectedMembersEvents = createSelector(
  selectedMembersSelector,
  idSelector,
  getSelectedMemberEvents
)

export const timezoneSelector = createSelector(
  calendarStepSelector,
  getUser.select(),
  ({ timezone }, { data: currentUser }) => timezone || currentUser.timezone
)

export const fetchedEventsStartRangeSelector = createSelector(
  calendarStepSelector,
  ({ fetchedEventsStartRange }) => fetchedEventsStartRange
)

export const eventTypeSelector = createSelector(
  ({ bookModal }) => bookModal.eventType,
  (eventType) => ({
    isSolo: eventType.kind === 'solo',
    isGroup: eventType.kind === 'group',
    isRoundRobin: eventType.pooling_type === 'round_robin',
    isCollective: eventType.pooling_type === 'collective',
    isCollectiveRR: eventType.pooling_type === 'multi_pool',
    isShared: eventType.owner_type === 'Organization',
    isTeam: eventType.owner_type === 'Page',
    isUser: eventType.owner_type === 'User',
    ...eventType,
  })
)

export const scheduledEventSelector = createSelector(
  ({ bookModal }) => bookModal.scheduledEvent,
  (scheduledEvent) => {
    // Don't use location if location_type === 'custom' and !location
    // See https://calendlyapp.atlassian.net/browse/EXT-3916?focusedCommentId=251586
    const location = scheduledEvent?.location?.trim()
    const locationType = scheduledEvent?.location_type
    const additionalInfo = scheduledEvent?.location_additional_info
    const useLocation = scheduledEvent?.location_type && (scheduledEvent.location_type !== 'custom' || location)
    const locationConfigurations = useLocation ? [{
      id: 1,
      kind: locationType,
      phone_number: location,
      additional_info: additionalInfo,
      location
    }] : []

    return {
      ...scheduledEvent,
      location_configurations: locationConfigurations,
      name: 'Follow-up',
      description: '',
    }
  }
)

export const hasCalendarErrorSelector = createSelector(
  eventTypeSelector,
  selectedMembersSelector,
  (eventType, members) => {
    if (!members.length) return false

    if (eventType.isRoundRobin) {
      return members.every((m) => m.calendarError)
    }

    if (eventType.isCollectiveRR) {
      return eventType.user_pools.some((pool) => {
        const poolMembers = members.filter((member) => member.user_pool_uuid === pool.uuid)
        return pool.pooling_type === 'collective'
          ? poolMembers.some((m) => m.calendarError)
          : poolMembers.every((m) => m.calendarError)
      })
    }

    return members.some((m) => m.calendarError)
  }
)

export const isNameSectionLockedSelector = createSelector(
  eventTypeSelector,
  (eventType) => Boolean(
    eventType.event_type_lock?.locking_rules?.some(
      (rule) => rule.name === 'v3_name_and_color'
    )
  )
)

export const isDurationSectionLockedSelector = createSelector(
  eventTypeSelector,
  (eventType) => Boolean(
    eventType.event_type_lock?.locking_rules?.some(
      (rule) => rule.name === 'v3_duration'
    )
  )
)

export const isAvailabilitySectionLockedSelector = createSelector(
  eventTypeSelector,
  (eventType) => Boolean(
    eventType.event_type_lock?.locking_rules?.some(
      (rule) => rule.name === 'v3_availability'
    )
  )
)

export const primaryInviteeSelector = createSelector(
  inviteesSelector,
  (invitees) => invitees.find((invitee) => invitee.primary)
)

export const selectedInviteeSelector = createSelector(
  inviteesSelector,
  (invitees) => invitees.find((invitee) => invitee.selected)
)

export const selectedHostSelector = createSelector(
  selectedMembersSelector,
  (members) => members.find((m) => m.selectedHost) || members[0]
)

export const questionFormValuesSelector = createSelector(
  loadingEventTypeSelector,
  eventTypeSelector,
  ({ bookModal }) => bookModal.calendarStep.questionFormValues,
  (loadingEventType, eventType, questionFormValues) => {
    if (questionFormValues) return questionFormValues
    if (loadingEventType) return {}

    return eventType.custom_fields.filter((field) => field.enabled)
      .sort((a, b) => a.position - b.position)
      .map((field) => ({
        answerChoices: field.answer_choices,
        includeOther: field.include_other,
        value: undefined,
        customValue: '',
        ...field
      }))
  }
)

export const inviteeQuestionsBookingSelector = createSelector(
  questionFormValuesSelector,
  (questionFormValues) => {
    if (!questionFormValues) return []
    return questionFormValues?.map((field) => {
      if (field.format === 'choices_many') {
        let value = field.value ? [...field.value] : []
        const hasCustomAnswer = field.includeOther && value.includes('custom_value')

        if (hasCustomAnswer) {
          const index = value.indexOf('custom_value')
          value[index] = field.customValue
        }
        value = value.join('\n')

        return {
          id: field.id,
          name: field.name,
          position: field.position,
          value,
          required: field.required
        }
      }
      return {
        id: field.id,
        name: field.name,
        position: field.position,
        value: field.format === 'choices_one' && field.value === 'custom_value' ? field.customValue : field.value,
        required: field.required
      }
    })
  }
)


export const sidebarFormValuesSelector = createSelector(
  loadingEventTypeSelector,
  eventTypeSelector,
  isOneOffBookingSelector,
  scheduledEventSelector,
  loadingScheduledEventSelector,
  ({ bookModal }) => bookModal.calendarStep.sidebarFormValues,
  (loadingEventType, eventType, isOneOff, scheduledEvent, loadingScheduledEvent, sidebarFormValues) => {
    if (sidebarFormValues) return sidebarFormValues

    if ((!isOneOff && loadingEventType) || (isOneOff && loadingScheduledEvent)) return {}

    const location = {
      isEditing: false,
      phoneCallKind: 'outbound_call',
      inviteePhoneNumber: '',
      hostPhoneNumber: '',
      hostPhoneDetails: '',
      physical: {
        location: '',
        details: '',
      },
      custom: {
        location: '',
      },
    }

    const event = isOneOff ? scheduledEvent : eventType

    if (event.location_configurations.length) {
      event.location_configurations.forEach((locationConfig, index) => {
        const isFirst = index === 0
        switch (locationConfig.kind) {
          case 'inbound_call':
            if (isFirst) {
              location.kind = 'phone_call'
            }
            location.phoneCallKind = locationConfig.kind
            location.hostPhoneNumber = locationConfig.phone_number
            location.hostPhoneDetails = locationConfig.additional_info
            break
          case 'outbound_call':
            if (isFirst) {
              location.kind = 'phone_call'
            }
            location.isEditing = !locationConfig.phone_number?.trim()
            location.inviteePhoneNumber = location.isEditing ? '' : locationConfig.phone_number
            location.phoneCallKind = locationConfig.kind
            break
          case 'physical':
            if (isFirst) {
              location.kind = `physical#${locationConfig.id}`
            }
            location[`physical#${locationConfig.id}`] = {
              location: locationConfig.location,
              details: locationConfig.additional_info,
            }
            break
          case 'custom':
            if (isFirst) {
              location.kind = `custom#${locationConfig.id}`
            }
            location[`custom#${locationConfig.id}`] = {
              location: locationConfig.location,
            }
            break
          case 'google_conference':
          case 'zoom_conference':
          case 'microsoft_teams_conference':
          case 'webex_conference':
          case 'gotomeeting_conference':
            if (isFirst) {
              location.kind = locationConfig.kind
            }
            break
          default:
            break
        }
      })
    }

    return {
      name: event.name || '',
      description: { html: event.description || '' },
      location
    }
  }
)

const integrationsSelector = createSelector(
  eventTypeSelector,
  scheduledEventSelector,
  selectedMembersSelector,
  (eventType, scheduledEvent, members) => {
    if (!eventType) return {}

    const event = (typeof eventType.is_zoom_configured !== 'undefined') ? eventType : scheduledEvent
    const conferenceOrganizer = members.find(({ isOrganizer }) => isOrganizer) || event
    const calendarIntegrationStatusSourceObject = eventType.pooling_type === 'collective' ? event : conferenceOrganizer

    return {
      google_conference:
        calendarIntegrationStatusSourceObject.google_conferencing_configured,
      microsoft_teams_conference:
        calendarIntegrationStatusSourceObject.is_microsoft_teams_configured,
      zoom_conference: conferenceOrganizer.is_zoom_configured,
      gotomeeting_conference: conferenceOrganizer.is_gotomeeting_configured,
      webex_conference: conferenceOrganizer.is_webex_configured,
    }
  }
)

export const enabledIntegrationsSelector = createSelector(
  integrationsSelector,
  (integrations) => Object.entries(integrations).reduce((acc, [integration, isEnabled]) => {
    if (isEnabled) {
      return [...acc, integration]
    }
    return acc
  }, [])
)

export const memberOrganizerSelector = createSelector(
  selectedMembersSelector,
  (members) => members.find(({ isOrganizer }) => isOrganizer)
)

const hostsScheduleSelector = createSelector(
  eventTypeSelector,
  hostsFieldSelector,
  (eventType, { selectedMembers }) => selectedMembers.map((member) => {
    const selectedRuleId = member.selected_rule_id
      || eventType.active_availability_rule_id
      || member.active_availability_rule_id
      || member.primary_availability_rule_id
    if (member.availabilityRules) {
      const rule = member.availabilityRules.find(
        (r) => r.id === selectedRuleId
      )
      if (rule) return rule
    }
    return { id: member.primary_availability_rule_id, rules: [] }
  })
)

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual)

const unavailabilityIntervalsSelector = createDeepEqualSelector(
  hostsScheduleSelector,
  timezoneSelector,
  datesRangeSelector,
  fetchedEventsDaysRangeSelector,
  currentTimeSelector,
  (schedules, targetTimezone, { rangeStart }, fetchedEventsDaysRange, currentTime) => {
    if (schedules.length === 0) {
      return {}
    }
    const currentDate = moment.tz(rangeStart, targetTimezone).startOf('day')
    const endOfRange = currentDate
      .clone()
      .add(fetchedEventsDaysRange - 1, 'days')
      .endOf('day')
    const unavailabilityRules = unavailabilityForDateRange(
      schedules,
      targetTimezone,
      currentDate,
      endOfRange,
      currentTime
    )

    while (currentDate.isBefore(endOfRange)) {
      const unavailabilityIntervals = unavailabilityRules[currentDate.format('YYYY-MM-DD')] || []
      unavailabilityIntervals.forEach((interval, index) => {
        const start = currentDate.clone()
        start.hour(interval.from.split(':')[0])
        start.minutes(interval.from.split(':')[1])

        const end = currentDate.clone()
        end.hour(interval.to.split(':')[0])
        end.minutes(interval.to.split(':')[1])

        unavailabilityRules[currentDate.format('YYYY-MM-DD')][index] = {
          start,
          end,
        }
      })

      currentDate.add(1, 'day')
    }
    return unavailabilityRules
  }
)

const calendarEventsSelector = createSelector(
  selectedMembersEvents,
  timezoneSelector,
  timeNotationSelector,
  currentTimeSelector,
  (events, timezone, timeNotation, currentTime) => calculateEventsOverlap(events).map(
    ({
      title,
      start,
      end,
      allDay,
      hostIndex,
      hostType,
      colorIndex,
      kind,
      isCalendlyEvent,
      userInitials,
      overlapCount,
      isDoubleBookable,
    }) => ({
      start: allDay ? moment.utc(start).format('YYYY-MM-DD') : start,
      end: allDay ? moment.utc(end).format('YYYY-MM-DD') : end,
      allDay,
      extendedProps: {
        title,
        hostIndex,
        hostType,
        colorIndex,
        kind,
        userInitials,
        overlapCount,
        timezone,
        isCalendlyEvent,
        isDoubleBookable,
        isPast: moment(end).isBefore(currentTime),
        duration: moment(end).diff(moment(start), 'minutes'),
        timeRange: timeRange({ start, end, timezone, timeNotation }),
        background: false,
      },
    })
  )
)

export const availableSpotsSelector = createSelector(
  unavailabilityIntervalsSelector,
  timezoneSelector,
  datesRangeSelector,
  timeNotationSelector,
  eventTypeDurationSelector,
  selectedSpotSelector,
  selectedMembersEvents,
  fetchedEventsDaysRangeSelector,
  () => true, // selectingSpotsEnabled
  () => true, // allowEventsOverlap
  () => true, // allowSpotsOverlap
  () => true, // allowInternalEventsOverlap
  getAvailableSpots
)

const backgroundUnavailableSpot = (
  currentDate,
  { start, end },
  timezone,
  timeNotation
) => ({
  display: 'background',
  title: '',
  start: start.toISOString(),
  end: end.toISOString(),
  extendedProps: {
    background: true,
    selectedSpot: false,
    timeRange: timeRange({ start, end, timezone, timeNotation }),
    disabled: true,
    available: false,
    timezone,
  },
})

const backgroundEventsSelector = createSelector(
  unavailabilityIntervalsSelector,
  timezoneSelector,
  datesRangeSelector,
  timeNotationSelector,
  selectedSpotSelector,
  fetchedEventsDaysRangeSelector,
  calendarEventsSelector,
  (
    unavailabilityRules,
    targetTimezone,
    { rangeStart },
    timeNotation,
    selectedSpot,
    fetchedEventsDaysRange,
    calendarEvents
  ) => {
    if (!rangeStart) {
      return []
    }
    const allDayEvents = calendarEvents.filter(({ allDay }) => allDay)
    const currentDate = moment.tz(rangeStart, targetTimezone).startOf('day')
    const endOfRange = currentDate
      .clone()
      .add(fetchedEventsDaysRange - 1, 'days')
      .endOf('day')

    const backgroundSpots = []
    while (currentDate.isBefore(endOfRange)) {
      const formattedDate = currentDate.format('YYYY-MM-DD')
      const unavailabilityIntervals = !allDayEvents.some(({ start }) => start === formattedDate)
        ? unavailabilityRules[formattedDate] || []
        : [
          {
            start: currentDate.clone().startOf('day'),
            end: currentDate.clone().endOf('day'),
          },
        ]

      unavailabilityIntervals.forEach((interval) => {
        const spot = backgroundUnavailableSpot(
          currentDate,
          interval,
          targetTimezone,
          timeNotation
        )
        backgroundSpots.push(spot)
      })

      currentDate.add(1, 'day')
    }

    if (selectedSpot) {
      const { start, end, invitees } = selectedSpot
      backgroundSpots.push({
        display: 'background',
        title: '',
        start: start.toISOString(),
        end: end.toISOString(),
        overlap: false,
        extendedProps: {
          background: true,
          selectedSpot: true,
          timeRange: timeRange({
            start,
            end,
            timezone: targetTimezone,
            timeNotation,
          }),
          available: true,
          timezone: targetTimezone,
          tooltipOpen: false,
          invitees,
          reserveTimes: false,
        },
      })
    }
    return backgroundSpots
  }
)

const currentTimeEventSelector = createSelector(
  currentTimeSelector,
  (currentTime) => {
    const now = currentTime.toISOString()

    return {
      title: '',
      display: 'background',
      overlap: true,
      start: now,
      end: now,
      classNames: ['customNowIndicator'],
      extendedProps: {
        isCurrentTime: true,
      },
    }
  }
)

export const eventsSelector = createSelector(
  calendarEventsSelector,
  backgroundEventsSelector,
  currentTimeEventSelector,
  (events, backgroundEvents, currentTimeEvent) => [
    ...events,
    ...backgroundEvents,
    currentTimeEvent,
  ]
)

const utcTodaySelector = createSelector(
  calendarListSelector,
  ({ today }) => today
)

const monthShiftSelector = createSelector(
  calendarListSelector,
  ({ monthShift }) => monthShift
)

const startMonthSelector = createSelector(
  utcTodaySelector,
  timezoneSelector,
  (today, timezone) => today
    .clone()
    .tz(timezone)
    .startOf('month'))

const calendarsSelector = createSelector(
  calendarListSelector,
  ({ calendars }) => calendars
)

export const loadingCalendarListDataSelector = createSelector(
  calendarListSelector,
  ({ loadingCalendarData }) => loadingCalendarData
)

export const todaySelector = createSelector(
  utcTodaySelector,
  timezoneSelector,
  (today, timezone) => today.clone().tz(timezone)
)

export const monthSelector = createSelector(
  startMonthSelector,
  monthShiftSelector,
  (startMonth, monthShift) => startMonth
    .clone()
    .add(monthShift, 'month')
)

export const startOfTheWeekSelector = createSelector(
  countryCodeSelector,
  (countryCode) => (WEEK_STARTS_ON_SUNDAY_COUNTRY_CODES.includes(countryCode) ? 'sunday' : 'monday')
)

export const calendarListCalendarSelector = createSelector(calendarListSelector,
  ({ calendar }) => calendar)

const currentCalendarSelector = createSelector(
  calendarsSelector,
  monthSelector,
  (calendars, month) => calendars[month.startOf('month').format('YYYY-MM-DD')] || {}
)

export const activeDayIdsSelector = createSelector(
  currentCalendarSelector,
  ({ days = [] }) => days
    .filter((day) => day.status === 'available')
    .map((day) => day.date)
)

export const daySelector = createSelector(
  calendarListCalendarSelector,
  ({ day }) => day
)

export const selectedDayCalendarSelector = createSelector(
  daySelector,
  calendarsSelector,
  (day, calendars) => {
    if (!day) return null
    const monthId = day.date.clone().startOf('month').format('YYYY-MM-DD')
    return calendars[monthId]
  }
)

export const spotsSelector = createSelector(
  daySelector,
  selectedDayCalendarSelector,
  (day, calendar) => {
    const spots = [...(calendar?.days.find((calendarDay) => calendarDay.date === day.id)?.spots || [])]
    return spots.filter((spot) => spot.status === 'available')
  }
)

export const spotAutofocusSelector = createSelector(
  calendarListSelector,
  ({ spotAutofocus }) => spotAutofocus
)

export const availabilityPreviewAttributesSelector = createSelector(
  eventTypeSelector,
  selectedMembersSelector,
  (eventType, selectedMembers) => {
    const result = {
      event_type_id: eventType.id,
      duration: eventType.duration,
      spot_step: eventType.spot_step,
      invitees_limit: eventType.invitees_limit,
      max_booking_time: eventType.max_booking_time,
      min_booking_time: eventType.min_booking_time,
      before_buffer_time: eventType.before_buffer_time,
      after_buffer_time: eventType.after_buffer_time,
      lock_timezone_enabled: eventType.lock_timezone_enabled,
      period_type: eventType.period_type === 'rollingDays' ? 'moving' : eventType.period_type,
      start_date: eventType.start_date,
      end_date: eventType.end_date,
    }

    const members = selectedMembers.map(camelize)

    if (members) {
      let activeMembers = members.filter((m) => m.userUuid !== null && m.enabled && m.deleted !== true)
      activeMembers = Object.values(determineMembers({ ...eventType, memberships: snakeize(members) })).map(camelize)
      const { event_type_memberships_attributes: membershipsAttributes } = membersToServerData(activeMembers)

      const membershipsServerData = membershipsAttributes.map((membershipAttribute) => {
        const { user_uuid: userUuid } = membershipAttribute
        const { holidays } = activeMembers.find((m) => m.userUuid === userUuid)
        const baseAttributes = {
          ...membershipAttribute,
          holidays: (holidays || []).map((holiday) => ({ id: holiday.id, enabled: holiday.enabled }))
        }

        return baseAttributes
      })

      result.event_type_memberships_attributes = membershipsServerData
    }

    return omitBy(result, isNil)
  }
)

export const draftInviteeSelector = createSelector(
  ({ bookModal }) => bookModal.calendarStep.draftInvitee,
  (draftInvitee) => draftInvitee
)
